import {
  Authorization,
  WebsocketMessageConfirmator,
} from '@collector/sportsapi-client-legacy'
import InvalidApplicationVersionAlert from '@mobile/components/Alert/InvalidApplicationVersionAlert.vue'
import { AppDatabase } from '@mobile/database/AppDatabase'
import { showAlert } from '@mobile/globalState/alert'
import * as scoutMessenger from '@mobile/globalState/scoutMessenger'
import * as sportsAPI from '@mobile/globalState/sportsAPI'
import { ScoutMessengerConnection } from '@mobile/ScoutMessengerConnection/ScoutMessengerConnection'

export async function getHighestPushMessageUt(
  database: AppDatabase,
): Promise<number> {
  const event = await database.events.getLast()
  const incident = await database.incidents.getLast()
  const error = await database.errors.getLast()
  const eventsScouts = await database.eventsScouts.getLast()

  const pushMessagesUts = [
    event?.pushMessageUt ?? 0,
    incident?.pushMessageUt ?? 0,
    error?.pushMessageUt ?? 0,
    eventsScouts?.pushMessageUt ?? 0,
  ]

  return Math.max(...pushMessagesUts)
}

async function connectToSocket(
  socket: ScoutMessengerConnection,
  token: string,
  database: AppDatabase,
): Promise<void> {
  if (!database.isOpen()) {
    socket.close()

    return
  }
  socket.connect(token, await getHighestPushMessageUt(database))
}

export function useScoutMessengerSocket(
  database: AppDatabase,
  authorization: Authorization,
): {
  socket: ScoutMessengerConnection
  wasReused: boolean
} {
  if (scoutMessenger.state.connection) {
    const messageConfirmator = scoutMessenger.state.messageConfirmator
    if (messageConfirmator) {
      sportsAPI.state.client.setMessageConfirmator(messageConfirmator)
    }

    return { socket: scoutMessenger.state.connection, wasReused: true }
  }

  const messageConfirmator = new WebsocketMessageConfirmator(10000, 10000)
  const socket = new ScoutMessengerConnection()

  if (authorization.currentJWT) {
    connectToSocket(socket, authorization.currentJWT, database)
  }

  const handleNewToken = (token: string): Promise<void> =>
    connectToSocket(socket, token, database)
  const handleRefreshTokenExpired = (): void => {
    socket.close()
  }

  authorization.onNewToken(handleNewToken)
  authorization.onRefreshTokenExpired(handleRefreshTokenExpired)

  const receiveEvent = messageConfirmator.receiveEvent.bind(messageConfirmator)
  const receiveLineup =
    messageConfirmator.receiveLineup.bind(messageConfirmator)
  const receiveIncident =
    messageConfirmator.receiveIncident.bind(messageConfirmator)
  const receiveError = messageConfirmator.receiveError.bind(messageConfirmator)

  database.events.on('anything', receiveEvent)
  database.lineups.on('anything', receiveLineup)
  database.incidents.on('anything', receiveIncident)
  database.errors.on('anything', receiveError)

  const dispose = (): void => {
    socket.dispose()
    authorization.offNewToken(handleNewToken)
    authorization.offRefreshTokenExpired(handleRefreshTokenExpired)
    database.events.off('anything', receiveEvent)
    database.lineups.off('anything', receiveLineup)
    database.incidents.off('anything', receiveIncident)
    database.errors.off('anything', receiveError)
  }

  socket.on('connected', () => {
    scoutMessenger.setAsConnected()
  })

  socket.on('authorizationFailed', async () => {
    await authorization.reauthorize()
  })

  socket.on('disconnected', () => {
    scoutMessenger.setAsNotConnected()
  })

  socket.on('synchronizationStarted', () => {
    scoutMessenger.setInitialSynchronizationAsStarted()
  })

  socket.on('synchronizationFinished', () => {
    scoutMessenger.setInitialSynchronizationAsFinished()
  })

  socket.on('invalidScoutMessengerVersion', () => {
    showAlert(InvalidApplicationVersionAlert)
  })

  scoutMessenger.setConnection(socket, dispose)
  scoutMessenger.setMessageConfirmator(messageConfirmator)
  sportsAPI.state.client.setMessageConfirmator(messageConfirmator)

  return { socket, wasReused: false }
}
